import React, {useEffect, useState, useCallback} from 'react';
import {Box, TextField, Stack, Typography, Button, IconButton, FormControlLabel, Checkbox} from '@mui/material';
import PropTypes from 'prop-types';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import {generateUUID} from '../../../../../utility/uuidUtil';

const MultiLineInput = ({
                            index,
                            componentId,
                            fieldKey,
                            value,
                            onChange,
                            descriptionHtml,
                            onDescriptionHtmlChange,
                            commitDescriptionHtml,
                            isEditMode,
                            enableValidation,
                            isFormArchived,
                            readonly,
                            placeholderValues,
                            accessLevel,
                            submitMode,
                            inputs = [{
                                id: generateUUID(),
                                title: '',
                                enableValidation: false
                            }],
                            error,
                            audience,
                            recipientType,
                            linkedToStudents,
                            linkedToEmployees,
                        }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleEditorBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const handleInputChange = (inputId, newValue) => {
        const newInputValues = {
            ...(value ?? {}),
            [inputId]: newValue
        };

        onChange({
            id: componentId,
            value: newInputValues
        });
    };

    const handleTitleChange = (inputId, newTitle) => {
        onChange({
            index,
            listKey: 'inputs',
            optionId: inputId,
            option: {title: newTitle},
        });
    };

    const handleValidationChange = (inputId, isRequired) => {
        onChange({
            index,
            listKey: 'inputs',
            optionId: inputId,
            option: {enableValidation: isRequired},
        });
    };

    const handleAddInput = () => {
        if (inputs.length >= 5) return;

        onChange({
            index,
            listKey: 'inputs',
            isAdd: true,
            option: {
                id: generateUUID(),
                title: '',
                enableValidation: false
            },
        });
    };

    const handleRemoveInput = (inputId) => {
        if (inputs.length <= 1) return;

        onChange({
            index,
            listKey: 'inputs',
            isRemove: true,
            optionId: inputId,
        });
    };

    return (
        <Box key={`MultiLineInput-${componentId}`} sx={{mt: 2}}>
            {isEditMode
                ? (
                    <>
                        <RichEditor
                            currentDescription={draftDescriptionHtml}
                            setCurrentDescription={handleDraftChange}
                            isFormLive={isFormArchived}
                            accessLevel={accessLevel}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            linkedToEmployees={linkedToEmployees}
                            placeholderValues={placeholderValues}
                            onFocus={() => setIsEditorFocused(true)}
                            onBlur={handleEditorBlur}
                        />
                        <Box mt={2}>
                            <Typography variant="subtitle1" gutterBottom>
                                Input Fields Configuration
                            </Typography>
                            <Stack spacing={2}>
                                {inputs.map((input) => (
                                    <Box
                                        key={`input-config-${input.id}`}
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                    >
                                        <TextField
                                            fullWidth
                                            label="Input Title"
                                            value={input.title || ''}
                                            onChange={(e) => handleTitleChange(input.id, e.target.value)}
                                            disabled={isFormArchived}
                                        />
                                        {!isFormArchived && !fieldKey && (
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={input.enableValidation || false}
                                                        onChange={(e) =>
                                                            handleValidationChange(input.id, e.target.checked)
                                                        }
                                                        disabled={isFormArchived}
                                                    />
                                                }
                                                label="Required"
                                            />
                                        )}
                                        {!isFormArchived && !fieldKey && inputs.length > 1 && (
                                            <IconButton
                                                onClick={() => handleRemoveInput(input.id)}
                                                size="small"
                                            >
                                                <DeleteIcon/>
                                            </IconButton>
                                        )}
                                    </Box>
                                ))}
                                {!isFormArchived && !fieldKey && (
                                    <Button
                                        startIcon={<AddIcon/>}
                                        onClick={handleAddInput}
                                        variant="outlined"
                                        size="small"
                                        disabled={inputs.length >= 5}
                                    >
                                        Add Input Field
                                    </Button>
                                )}
                            </Stack>
                        </Box>
                    </>
                )
                : (
                    <>
                        <RichEditorResultText
                            id={componentId}
                            description={descriptionHtml}
                            placeholderValues={placeholderValues}
                            readonly={readonly}
                            enableValidation={enableValidation}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            linkedToEmployees={linkedToEmployees}
                            submitMode={submitMode}
                        />
                        {!readonly
                            ? (
                                <Stack direction="row" spacing={2}>
                                    {inputs.map((input, idx) => {
                                        const inputValue = value?.[input.id] || '';
                                        return (
                                            <TextField
                                                key={`input-${input.id}`}
                                                fullWidth
                                                required={input.enableValidation}
                                                value={inputValue}
                                                disabled={!submitMode}
                                                onChange={(e) =>
                                                    handleInputChange(input.id, e.target.value)
                                                }
                                                label={input.title || `Input ${idx + 1}`}
                                                error={!!error?.[input.id]}
                                                helperText={error?.[input.id]}
                                            />
                                        );
                                    })}
                                </Stack>
                            )
                            : (
                                <Stack direction="row" spacing={2}>
                                    {inputs.map((input, idx) => {
                                        const inputValue = value?.[input.id] || '';
                                        return (
                                            <TextField
                                                key={`readonly-${input.id}`}
                                                fullWidth
                                                multiline
                                                defaultValue={inputValue}
                                                label={input.title || `Input ${idx + 1}`}
                                                InputProps={{
                                                    readOnly: true,
                                                }}
                                            />
                                        );
                                    })}
                                </Stack>
                            )}
                    </>
                )}
        </Box>
    );
};

MultiLineInput.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    fieldKey: PropTypes.string,
    value: PropTypes.object,
    index: PropTypes.number,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    onInputsChange: PropTypes.func.isRequired,
    enableValidation: PropTypes.bool,
    onChange: PropTypes.func,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readonly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    submitMode: PropTypes.bool,
    inputs: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            title: PropTypes.string,
            enableValidation: PropTypes.bool,
        })
    ),
    error: PropTypes.object,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    linkedToEmployees: PropTypes.bool,
};

export default MultiLineInput;
